import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import _ from 'underscore'
import styled from 'styled-components'

import DeviceMetricModel from 'models/DeviceMetric'

import Form from 'pages/shared/Form'
import ChartLimitTable from 'pages/shared/DeviceMetricForm/ChartLimitTable'
import MetricFields from 'pages/shared/DeviceMetricForm/MetricFields'

import { useStoreContext } from 'hooks/StoreContext'
import useDeviceMetrics from 'hooks/useDeviceMetrics'
import useTrends from 'hooks/useTrends'

import { Metric, DeviceMetricFormErrors } from 'types/DeviceMetric'

import { parseIntoDescriptionsAndDetails, convertToTextareaValue } from 'helpers/alerts'

import { useConfirmDialog, useAlertDialog } from 'hooks/useDialog'

import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

interface props {
  validatedSaveType: string,
  deviceMetric: Omit<Metric, 'type'>
}

interface MetricValues extends Omit<Metric, 'callToAction'> {
  callToAction: string;
}

const deviceMetricModel = new DeviceMetricModel()

const DeviceMetricForm = ({ validatedSaveType, deviceMetric }: props) => {
  const navigate = useNavigate()
  useDeviceMetrics()
  useTrends()

  const { deviceMetrics, setAreDeviceMetricsLoaded, trends } = useStoreContext()
  const charts = deviceMetric.charts
  const hasDeviceMetricStatuses = deviceMetric.hasMetricStatuses
  const hasDeviceMetricValues = deviceMetric.hasMetricValues
  const displayDeleteButton = validatedSaveType === 'update' && charts.length === 0 && !hasDeviceMetricStatuses && !hasDeviceMetricValues

  const [deviceMetricAttributes, setDeviceMetricAttributes] = useState<Omit<MetricValues, 'type'>>({
    ...deviceMetric,
    callToAction: convertToTextareaValue(deviceMetric.callToAction)
  })

  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [errors, setErrors] = useState<DeviceMetricFormErrors>({ id: '', name: '', description: '', callToAction: '', color: '', unit: '', draft: '' })
  const { confirm } = useConfirmDialog()
  const { alert } = useAlertDialog()
  const componentType = 'Device Metric'

  const onSaveCallback = useCallback(() => {
    setDeviceMetricAttributes({ ...deviceMetricAttributes })
    setIsSaving(true)
    const onSaveSuccessCallback = () => {
      setAreDeviceMetricsLoaded(false)
      navigate('/admin/metrics')
    }

    const onValidationErrorCallback = (errors) => {
      setErrors(errors)
      setIsSaving(false)
    }

    const onSaveFailureCallBack = () => {
      setIsSaving(false)
      alert({ title: CONFIRMATION_DIALOG_MESSAGES.ERROR_MESSAGE.SAVE_FAILED, description: '' })
    }
    deviceMetricModel.save(deviceMetrics, trends, validatedSaveType, {
      ...deviceMetricAttributes,
      callToAction: parseIntoDescriptionsAndDetails(deviceMetricAttributes.callToAction)
    }, onSaveSuccessCallback, onSaveFailureCallBack, onValidationErrorCallback)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceMetricAttributes, validatedSaveType, setAreDeviceMetricsLoaded, navigate])

  const onCancelCallback = useCallback(async () => {
    const hasChanges = !_.isEqual(deviceMetricAttributes, { ...deviceMetric })

    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.CANCEL.TITLE,
      description: CONFIRMATION_DIALOG_MESSAGES.CANCEL.DESCRIPTION,
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.OK
    })

    if (!hasChanges || (hasChanges && isConfirmed)) {
      navigate('/admin/metrics')
    }
  }, [navigate, deviceMetric, deviceMetricAttributes, confirm])

  const onDeviceMetricUpdateCallback = useCallback((updatedAttributes) => {
    setDeviceMetricAttributes({ ...deviceMetricAttributes, ...updatedAttributes })
  }, [deviceMetricAttributes])

  const onDeleteCallback = useCallback(async () => {
    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.DELETE.TITLE(componentType),
      description: CONFIRMATION_DIALOG_MESSAGES.DELETE.DESCRIPTION(componentType),
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.DELETE
    })

    if (isConfirmed) {
      setIsDeleting(true)
      const onDeleteSuccessCallback = () => {
        setAreDeviceMetricsLoaded(false)
        navigate('/admin/metrics')
      }

      const onDeleteFailureCallback = (errorMessage) => {
        setIsDeleting(false)
        alert({ title: errorMessage, description: '' })
      }

      deviceMetricModel.delete(onDeleteSuccessCallback, onDeleteFailureCallback, deviceMetric)
    }
  }, [navigate, deviceMetric, setAreDeviceMetricsLoaded, confirm, alert])

  return (
    <S.Form
      deleteButtonText="Metric"
      isSaving={isSaving}
      isDeleting={isDeleting}
      displayDeleteButton={displayDeleteButton}
      onSaveCallback={onSaveCallback}
      onDeleteCallback={onDeleteCallback}
      onCancelCallback={onCancelCallback}
    >
      <MetricFields
        validatedSaveType={validatedSaveType}
        id={deviceMetricAttributes.id}
        name={deviceMetricAttributes.name}
        description={deviceMetricAttributes.description}
        callToAction={deviceMetricAttributes.callToAction}
        unit={deviceMetricAttributes.unit}
        draft={deviceMetricAttributes.draft}
        color={deviceMetricAttributes.color}
        onChangeCallback={onDeviceMetricUpdateCallback}
        errors={errors}
      />
      <br />
      {
        validatedSaveType === 'update' &&
          <>
            <h2> Charts </h2>
            <ChartLimitTable charts={charts} />
          </>
      }

    </S.Form>
  )
}

export default DeviceMetricForm

const S = {
  Form: styled(Form)`
    max-width: 100%;
  `
}
